import * as React from 'react'
import type { TranscriptUtterance } from '@/client/types.gen'

export interface MatchRange {
	start: number
	end: number
}

/**
 * Returns the 0-based index of the utterance containing the given character offset
 * in the searchable content (each line: "Speaker: text\n"), or -1 if none.
 */
export function getUtteranceIndexForOffset(
	utterances: TranscriptUtterance[],
	editedUtterances: Map<number, string>,
	editedSpeakers: Map<number, string>,
	offset: number
): number {
	let textOffset = 0
	for (let i = 0; i < utterances.length; i++) {
		const utteranceText = editedUtterances.get(i) ?? utterances[i].text
		const effectiveSpeaker = editedSpeakers.get(i) ?? utterances[i].speaker
		const prefixLength = effectiveSpeaker.length + 2
		const totalLength = prefixLength + utteranceText.length

		if (offset >= textOffset + prefixLength && offset < textOffset + totalLength) {
			return i
		}
		textOffset += totalLength + 1
	}
	return -1
}

export interface UseTranscriptReplaceParams {
	utterances: TranscriptUtterance[] | null
	editedUtterances: Map<number, string>
	editedSpeakers: Map<number, string>
	matchRanges: MatchRange[]
	clampedMatchIndex: number
	replaceQuery: string
	updateUtterance: (index: number, text: string, speaker?: string) => void
	setCurrentMatchIndex: React.Dispatch<React.SetStateAction<number>>
}

export function useTranscriptReplace({
	utterances,
	editedUtterances,
	editedSpeakers,
	matchRanges,
	clampedMatchIndex,
	replaceQuery,
	updateUtterance,
	setCurrentMatchIndex,
}: UseTranscriptReplaceParams) {
	const handleReplace = React.useCallback(() => {
		if (!utterances || matchRanges.length === 0) return

		const matchRange = matchRanges[clampedMatchIndex]
		const currentUtteranceIdx = getUtteranceIndexForOffset(
			utterances,
			editedUtterances,
			editedSpeakers,
			matchRange.start
		)
		if (currentUtteranceIdx === -1) return

		let textOffset = 0
		for (let i = 0; i < currentUtteranceIdx; i++) {
			const utteranceText = editedUtterances.get(i) ?? utterances[i].text
			const effectiveSpeaker = editedSpeakers.get(i) ?? utterances[i].speaker
			textOffset += effectiveSpeaker.length + 2 + utteranceText.length + 1
		}

		const utteranceText =
			editedUtterances.get(currentUtteranceIdx) ?? utterances[currentUtteranceIdx].text
		const effectiveSpeaker =
			editedSpeakers.get(currentUtteranceIdx) ?? utterances[currentUtteranceIdx].speaker
		const prefixLength = effectiveSpeaker.length + 2
		const localStart = matchRange.start - (textOffset + prefixLength)
		const localEnd = matchRange.end - (textOffset + prefixLength)

		const before = utteranceText.slice(0, localStart)
		const after = utteranceText.slice(localEnd)
		const newText = before + replaceQuery + after

		updateUtterance(currentUtteranceIdx, newText)
		setCurrentMatchIndex(Math.max(0, Math.min(clampedMatchIndex, matchRanges.length - 2)))
	}, [
		utterances,
		editedUtterances,
		editedSpeakers,
		matchRanges,
		clampedMatchIndex,
		replaceQuery,
		updateUtterance,
		setCurrentMatchIndex,
	])

	const handleReplaceAll = React.useCallback(() => {
		if (!utterances || matchRanges.length === 0) return

		let textOffset = 0

		for (let i = 0; i < utterances.length; i++) {
			const utteranceText = editedUtterances.get(i) ?? utterances[i].text
			const effectiveSpeaker = editedSpeakers.get(i) ?? utterances[i].speaker
			const prefixLength = effectiveSpeaker.length + 2
			const textStart = textOffset + prefixLength
			const textEnd = textStart + utteranceText.length

			const utteranceMatches = matchRanges.filter((m) => m.start >= textStart && m.end <= textEnd)

			if (utteranceMatches.length > 0) {
				let updatedText = utteranceText
				for (let j = utteranceMatches.length - 1; j >= 0; j--) {
					const m = utteranceMatches[j]
					const localStart = m.start - textStart
					const localEnd = m.end - textStart
					updatedText = updatedText.slice(0, localStart) + replaceQuery + updatedText.slice(localEnd)
				}
				updateUtterance(i, updatedText)
			}

			textOffset += prefixLength + utteranceText.length + 1
		}

		setCurrentMatchIndex(0)
	}, [
		utterances,
		editedUtterances,
		editedSpeakers,
		matchRanges,
		replaceQuery,
		updateUtterance,
		setCurrentMatchIndex,
	])

	return { handleReplace, handleReplaceAll }
}
